import React, { useEffect, useState, useMemo } from 'react';
import { postAPI } from '../../../app/api';
import { store } from '../../../app/store';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../../../UIComponents/Loader';
import PageActions from '../../Universal/PageActions';

export default function PermissionPopup({ userId, show, onClose }) {
    const store_data = JSON.parse(store());
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedModule, setSelectedModule] = useState('All');
    const [expandedModules, setExpandedModules] = useState({});

    // Group actions by module
    const groupedActions = useMemo(() => {
        return PageActions.reduce((acc, item) => {
            if (!acc[item.module]) {
                acc[item.module] = [];
            }
            acc[item.module].push(item);
            return acc;
        }, {});
    }, []);

    // Get unique modules
    const modules = useMemo(() => ['All', ...Object.keys(groupedActions)], [groupedActions]);

    useEffect(() => {
        if (show && userId) {
            fetchPermissions();
            // Expand all modules by default
            const expanded = {};
            Object.keys(groupedActions).forEach(module => {
                expanded[module] = true;
            });
            setExpandedModules(expanded);
        }
        // eslint-disable-next-line
    }, [show, userId]);

    const fetchPermissions = async () => {
        setLoading(true);
        try {
            // Initialize permissions array with all pages
            const initialPermissions = PageActions.map(pageAction => ({
                module: pageAction.module,
                page: pageAction.page,
                availableActions: pageAction.actions,
                selectedActions: []
            }));

            // Fetch user's current permissions from backend
            const response = await postAPI('/users/get-permissions', { user_id: userId, user: store_data });
            
            if (response?.status === 1 && response.data) {
                // Map backend permissions to our structure
                response.data.forEach(perm => {
                    const index = initialPermissions.findIndex(
                        p => p.module === perm.module && p.page === perm.page
                    );
                    
                    if (index === -1) {
                        // If not found, try to match by action
                        const matchIndex = initialPermissions.findIndex(p => 
                            p.availableActions.includes(perm.action)
                        );
                        
                        if (matchIndex !== -1 && perm.allow === 1) {
                            if (!initialPermissions[matchIndex].selectedActions.includes(perm.action)) {
                                initialPermissions[matchIndex].selectedActions.push(perm.action);
                            }
                        }
                    } else if (perm.allow === 1 && initialPermissions[index].availableActions.includes(perm.action)) {
                        if (!initialPermissions[index].selectedActions.includes(perm.action)) {
                            initialPermissions[index].selectedActions.push(perm.action);
                        }
                    }
                });
            }

            setPermissions(initialPermissions);
        } catch (error) {
            console.error('Error fetching permissions:', error);
            toast.error('Failed to fetch permissions');
        } finally {
            setLoading(false);
        }
    };

    // Toggle action for a specific page
    const toggleAction = (module, page, action) => {
        setPermissions(prev => prev.map(perm => {
            if (perm.module === module && perm.page === page) {
                const selected = perm.selectedActions.includes(action)
                    ? perm.selectedActions.filter(a => a !== action)
                    : [...perm.selectedActions, action];
                return { ...perm, selectedActions: selected };
            }
            return perm;
        }));
    };

    // Select/Deselect all actions for a page
    const toggleAllPageActions = (module, page, select) => {
        setPermissions(prev => prev.map(perm => {
            if (perm.module === module && perm.page === page) {
                return { ...perm, selectedActions: select ? perm.availableActions : [] };
            }
            return perm;
        }));
    };

    // Select/Deselect all pages in a module
    const toggleAllModulePages = (module, select) => {
        setPermissions(prev => prev.map(perm => {
            if (perm.module === module) {
                return { ...perm, selectedActions: select ? perm.availableActions : [] };
            }
            return perm;
        }));
    };

    // Save all permissions
    const savePermissions = async () => {
        setSaving(true);
        try {
            // Send all permissions at once using bulk update endpoint
            const response = await postAPI('/users/set-permissions', {
                user_id: userId,
                permissions: permissions,
                user: store_data
            });
            
            if (response?.status === 1) {
                toast.success(`Permissions updated successfully! (${response.count || 0} permissions set)`);
                // setTimeout(() => {
                //     onClose();
                // }, 1000);
            } else {
                toast.error(response?.error || 'Failed to save permissions');
            }
        } catch (error) {
            console.error('Error saving permissions:', error);
            toast.error('Failed to save permissions');
        } finally {
            setSaving(false);
        }
    };

    // Filter permissions by search and module
    const filteredPermissions = useMemo(() => {
        return permissions.filter(perm => {
            const matchesSearch = searchTerm === '' || 
                perm.page.toLowerCase().includes(searchTerm.toLowerCase()) ||
                perm.module.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesModule = selectedModule === 'All' || perm.module === selectedModule;
            return matchesSearch && matchesModule;
        });
    }, [permissions, searchTerm, selectedModule]);

    // Group filtered permissions by module
    const filteredGrouped = useMemo(() => {
        return filteredPermissions.reduce((acc, perm) => {
            if (!acc[perm.module]) {
                acc[perm.module] = [];
            }
            acc[perm.module].push(perm);
            return acc;
        }, {});
    }, [filteredPermissions]);

    return (
        <div className={`modal fade ${show ? 'show d-block' : ''}`} tabIndex="-1" style={{ background: show ? 'rgba(0,0,0,0.5)' : 'none' }}>
            <div className="modal-dialog modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Manage User Permissions</h5>
                        <button type="button" className="btn-close" onClick={onClose} disabled={saving}></button>
                    </div>
                    <div className="modal-body">
                        {loading ? (
                            <Loader />
                        ) : (
                            <>
                                {/* Search and Filter */}
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search by page or module..."
                                            value={searchTerm}
                                            onChange={e => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <select
                                            className="form-select"
                                            value={selectedModule}
                                            onChange={e => setSelectedModule(e.target.value)}
                                        >
                                            {modules.map(module => (
                                                <option key={module} value={module}>{module}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Permissions Table */}
                                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                    {Object.keys(filteredGrouped).length === 0 ? (
                                        <p className="text-center text-muted">No permissions found</p>
                                    ) : (
                                        Object.keys(filteredGrouped).map(module => (
                                            <div key={module} className="mb-4">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <h6 
                                                        className="mb-0 cursor-pointer"
                                                        onClick={() => setExpandedModules(prev => ({
                                                            ...prev,
                                                            [module]: !prev[module]
                                                        }))}
                                                    >
                                                        <i className={`fa fa-chevron-${expandedModules[module] ? 'down' : 'right'} me-2`}></i>
                                                        <strong>{module}</strong> ({filteredGrouped[module].length} pages)
                                                    </h6>
                                                    <div>
                                                        <button
                                                            className="btn btn-sm btn-success me-2"
                                                            onClick={() => toggleAllModulePages(module, true)}
                                                            disabled={saving}
                                                        >
                                                            Select All
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-secondary"
                                                            onClick={() => toggleAllModulePages(module, false)}
                                                            disabled={saving}
                                                        >
                                                            Deselect All
                                                        </button>
                                                    </div>
                                                </div>

                                                {expandedModules[module] && (
                                                    <table className="table table-bordered table-sm">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th style={{ width: '200px' }}>Page</th>
                                                                <th>Actions</th>
                                                                <th style={{ width: '120px' }}>Quick Select</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filteredGrouped[module].map((perm, idx) => (
                                                                <tr key={`${perm.module}-${perm.page}-${idx}`}>
                                                                    <td><strong>{perm.page}</strong></td>
                                                                    <td>
                                                                        <div className="d-flex flex-wrap gap-2">
                                                                            {perm.availableActions.map(action => (
                                                                                <label key={action} className="form-check-label me-3">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        className="form-check-input me-1"
                                                                                        checked={perm.selectedActions.includes(action)}
                                                                                        onChange={() => toggleAction(perm.module, perm.page, action)}
                                                                                        disabled={saving}
                                                                                    />
                                                                                    {action}
                                                                                </label>
                                                                            ))}
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-primary me-1"
                                                                            onClick={() => toggleAllPageActions(perm.module, perm.page, true)}
                                                                            disabled={saving}
                                                                        >
                                                                            All
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-secondary"
                                                                            onClick={() => toggleAllPageActions(perm.module, perm.page, false)}
                                                                            disabled={saving}
                                                                        >
                                                                            None
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={onClose}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-primary" 
                            onClick={savePermissions}
                            disabled={saving || loading}
                        >
                            {saving ? 'Saving...' : 'Save Permissions'}
                        </button>
                    </div>
                </div>
            </div>
            <ToastContainer />
        </div>
    );
}
